import type { ReactElement } from "react"
import { useContext, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import { DeleteOutline } from "@mui/icons-material"
import { Box, Grid, Switch } from "@mui/material"
import { Button, InputText, Pagination, Select, Typography } from "@synapse-analytics/synapse-ui"
import type { VisibilityState } from "@tanstack/react-table"
import { capitalize } from "lodash"
import type { MRT_ColumnDef } from "material-react-table"
import { v4 as uuidv4 } from "uuid"

import { Tick } from "../../../../../components/status/Tick"
import { BaseTable } from "../../../../../components/tables/BaseTable"
import { useResizeObserver } from "../../../../../hooks/useResizeObserver"
import type { WorkflowInputSchema } from "../../../../../types/custom/projects"
import { TableConfigurationRequest } from "../../../../../types/generated/api/TableConfigurationRequest"
import { WorkflowSchemaFeatureRequest } from "../../../../../types/generated/api/WorkflowSchemaFeatureRequest"
import { schemaFeatureTypes } from "../../../../../utils/conditionHelpers"
import { WorkflowSchemaContext } from "../../../context/Context"
import type { InputFeaturesProps, SchemaFeature } from "../../../types/interfaces"

import styles from "./InputFeatures.module.scss"

/**
 * InputFeatures component that manages workflow input features configuration.
 * Supports feature editing, type selection, and conflict resolution.
 */
export function InputFeatures(props: Readonly<InputFeaturesProps>): ReactElement {
  // Prop destructuring
  const { formik, schema, isWorkflowsLoading, currentMode, rowsPerPage, setRowsPerPage } = props

  // Context hooks
  const { setStep, conflictsList } = useContext(WorkflowSchemaContext)

  // Custom hooks
  const [tableContainerRef, { height }] = useResizeObserver()

  // Translation hook
  const { t } = useTranslation("workflows", {
    keyPrefix: "workflowSchemaDialog.inputFeatures",
  })

  // State hooks
  // Pagination and search state for table view
  const [page, setPage] = useState<number>(0)
  const [search, setSearch] = useState<string | undefined>(undefined)

  // Computed values
  // Create an array that includes both the feature data and its original index
  const filteredFeaturesWithIndex = formik.values.features.reduce(
    (acc, feature, originalIndex) => {
      if (!search || feature?.name?.toLowerCase().includes(search.toLowerCase())) {
        acc.push({ ...feature, originalIndex })
      }
      return acc
    },
    [] as (SchemaFeature & { originalIndex: number })[],
  )

  const paginatedFeaturesWithIndex = filteredFeaturesWithIndex
    // First, paginate the features by slicing based on current page and rows per page
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  const columns: MRT_ColumnDef<WorkflowInputSchema>[] = [
    {
      header: t("table.headers.featureName"),
      accessorKey: "name",
      Cell: ({ row }) => {
        // Use the original index from the full features array
        const feature = paginatedFeaturesWithIndex[row.index]
        const originalIndex = feature?.originalIndex

        return (
          <Grid item xs={12}>
            {currentMode === "read" ||
            (currentMode !== "create" &&
              !feature?.new &&
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              schema?.find((feat: any) => feat?.name === feature?.name)?.source !== "workflow") ||
            (currentMode === "edit" && !feature?.new) ? (
              <Typography variant="p">{feature?.name || ""}</Typography>
            ) : (
              <InputText
                fullWidth
                size="small"
                id={`features[${originalIndex}].name`}
                placeholder={t("table.columns.featureName.inputPlaceholder")}
                key={feature?.id}
                value={feature?.name}
                handleChange={formik.handleChange}
                handleBlur={formik.handleBlur}
                hideDescription
              />
            )}
          </Grid>
        )
      },
    },
    {
      header: t("table.headers.type"),
      accessorKey: "type",
      Cell: ({ row }) => {
        const feature = paginatedFeaturesWithIndex[row.index]
        const originalIndex = feature?.originalIndex

        return (
          <Grid container item xs={12} flexWrap="nowrap">
            <Grid item container>
              {currentMode === "read" ? (
                <Typography style={{ textTransform: "capitalize" }} variant="p">
                  {feature?.type?.toLowerCase()}
                </Typography>
              ) : (
                <Select
                  hideDescription
                  size="small"
                  id={`features[${originalIndex}].type`}
                  value={feature?.type}
                  handleChange={(e) => formik.setFieldValue(`features[${originalIndex}].type`, e.target.value)}
                  disabled={
                    (currentMode !== "create" &&
                      !feature?.new &&
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      schema?.find((feat: any) => feat?.name === feature?.name)?.source !== "workflow") ||
                    (currentMode === "edit" &&
                      !feature?.new &&
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      schema?.find((feat: any) => feat?.name === feature?.name)?.type !==
                        WorkflowSchemaFeatureRequest.type.UNDEFINED)
                  }
                  fullWidth
                  type="text"
                  optionsWithValues={schemaFeatureTypes.map((feature) => ({
                    label: capitalize(feature),
                    value: feature,
                  }))}
                  placeholder={t("table.columns.type.selectPlaceholder")}
                />
              )}
            </Grid>
          </Grid>
        )
      },
    },
    {
      header: t("table.headers.required"),
      accessorKey: "required",
      Cell: ({ row }) => {
        const feature = paginatedFeaturesWithIndex[row.index]
        const originalIndex = feature?.originalIndex

        return currentMode === "read" ? (
          <Tick variant={feature?.is_required ? "positive" : "base"} />
        ) : (
          <Box display="flex" justifyContent="flex-start" margin="0px 4px 0px 0px">
            <Switch
              sx={{
                "& .MuiSwitch-thumb": {
                  width: "15px",
                  height: "15px",
                },
              }}
              size="small"
              onChange={() => formik.setFieldValue(`features[${originalIndex}].is_required`, !feature?.is_required)}
              checked={feature?.is_required ?? false}
              disabled={
                currentMode !== "create" &&
                !feature?.new &&
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                schema?.find((feat: any) => feat?.name === feature?.name)?.source !== "workflow"
              }
            />
          </Box>
        )
      },
    },
    {
      header: t("table.headers.source"),
      accessorKey: "source",
      Cell: ({ row }) => {
        const feature = paginatedFeaturesWithIndex[row.index]

        // Replace underscores with spaces
        const stringWithSpaces = feature?.source?.replace(/_/g, " ")
        // Replace "and" (with or without underscores) with plus sign
        const stringWithPlus = stringWithSpaces?.replace(/\band\b|_and_|_and\b|\band_/g, "+")

        return (
          <Box display="flex" justifyContent="flex-start" margin="0px 4px 0px 0px">
            <Typography variant="p">{stringWithPlus ?? "workflow"}</Typography>
          </Box>
        )
      },
    },
    {
      header: t("table.headers.action"),
      accessorKey: "remove",
      Cell: ({ row }) => {
        const feature = paginatedFeaturesWithIndex[row.index]
        const originalIndex = feature?.originalIndex

        const isButtonDisabled = Boolean(
          currentMode === "read" ||
            (currentMode !== "create" &&
              !feature?.new &&
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              schema?.find((feat: any) => feat?.name === feature?.name)?.source !== "workflow"),
        )

        return (
          <Grid item xs={12} container flexWrap={"nowrap"}>
            <Button
              tooltip={
                isButtonDisabled && currentMode === "edit"
                  ? feature?.source !== "workflow"
                    ? t("table.columns.actions.tooltips.cannotRemoveUsedFeature")
                    : t("table.columns.actions.tooltips.cannotRemoveRequiredFeature")
                  : ""
              }
              tooltipPlacement="bottom"
              variant={"ghost"}
              size="small"
              onClick={() => {
                // Remove from the original features array using the original index
                const newFeatures = formik.values.features.filter((_, index) => index !== originalIndex)
                formik.setFieldValue("features", newFeatures)

                // Adjust page if we deleted the last item on the current page
                const newFilteredCount = newFeatures.filter((feature) =>
                  search ? feature?.name?.toLowerCase().includes(search.toLowerCase()) || feature?.name === "" : true,
                ).length
                const maxPage = Math.max(0, Math.ceil(newFilteredCount / rowsPerPage) - 1)
                if (page > maxPage) {
                  setPage(maxPage)
                }
              }}
              className={styles.iconButton}
              disabled={isButtonDisabled}
            >
              <DeleteOutline
                style={{
                  fontSize: "16px",
                  fill: !isButtonDisabled
                    ? "var(--negative-icon-default) !important"
                    : "var(--disabled-icon-default) !important",
                }}
              />
            </Button>
          </Grid>
        )
      },
    },
  ]

  const columnVisibility: VisibilityState | undefined = useMemo(() => {
    switch (currentMode) {
      case "create":
      case "edit":
        return {
          name: true,
          type: true,
          required: true,
          source: true,
          remove: true,
        }
      case "read":
        return { name: true, type: true, required: true, source: true, remove: false }
      default:
        return undefined
    }
  }, [currentMode])

  useEffect(() => {
    if (height && tableContainerRef.current) {
      const headerEl = tableContainerRef.current.querySelector<HTMLElement>(".MuiTableHead-root")
      const rowEl = tableContainerRef.current.querySelector<HTMLElement>("tbody .MuiTableRow-root")

      const headerHeight = headerEl?.offsetHeight ?? 33
      const rowHeight = rowEl?.offsetHeight ?? 36

      if (rowHeight > 0) {
        const tableBodyHeight = height - headerHeight
        const newRowsPerPage = Math.floor(tableBodyHeight / rowHeight)

        if (newRowsPerPage > 0) {
          setRowsPerPage(newRowsPerPage)
        } else {
          setRowsPerPage(10) // Ensure at least one row is shown
        }
      }
    }
    // Rerun when data changes to get row height when data appears.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height, tableContainerRef])

  return (
    <Grid item container gap={2} xs direction={"column"} flexWrap={"nowrap"}>
      <Grid container item justifyContent={"space-between"} alignItems="center" flexWrap={"nowrap"}>
        <Grid item>
          <InputText
            variant="filled"
            placeholder={t("searchPlaceholder")}
            hideDescription
            value={search}
            type="text"
            handleChange={(e) => {
              setSearch(e.target.value as string)
              setPage(0) // Reset to first page when search changes
            }}
          />
        </Grid>

        {currentMode !== "read" && (
          <Grid item container gap={1.5} width={"fit-content"}>
            <Button
              size="small"
              variant="secondary"
              onClick={() => {
                formik.setFieldValue("features", [
                  { name: "", type: "TEXT", is_required: true, remove: false, id: uuidv4(), new: true },
                  ...formik.values.features,
                ])
                setPage(0)
              }}
              disabled={!!search || Object.keys(conflictsList.unUsedFeatures).length > 0}
              tooltip={search ? t("addFeatureButton.tooltip") : ""} // Disable button if search is active
            >
              {t("addFeatureButton.text")}
            </Button>

            <Button
              size="small"
              variant="primary-inverse"
              onClick={() => setStep(1)}
              disabled={Object.keys(conflictsList.unUsedFeatures).length > 0}
            >
              {t("uploadFile")}
            </Button>
          </Grid>
        )}
      </Grid>

      <Grid item xs={12} ref={tableContainerRef}>
        <BaseTable
          newStyling
          disableToolbar
          disablePagination
          enableOrdering={false}
          title={t("table.title")}
          pagination={{
            pageIndex: 0,
            pageSize: rowsPerPage,
          }}
          source={TableConfigurationRequest.table.WORKFLOW_SCHEMA}
          columns={(columns as MRT_ColumnDef[]) ?? []}
          data={paginatedFeaturesWithIndex ?? []} // Use the paginated data with original indices
          isLoading={currentMode === "read" && isWorkflowsLoading}
          columnsVisibility={columnVisibility}
        />
      </Grid>

      {filteredFeaturesWithIndex.length > 0 && (
        <Pagination
          page={page + 1}
          onPageChange={(page: number) => setPage(page - 1)}
          pageSize={rowsPerPage}
          totalCount={filteredFeaturesWithIndex.length} // Use filtered count, not paginated
          variant={"simple"}
          size="small"
          hidePageSize
          maxVisiblePages={5}
        />
      )}
    </Grid>
  )
}
